import { createContext, useContext, useState, useEffect } from "react";
import API from "@/lib/api";

// Separate context from admin AuthContext. Uses portal_* localStorage keys
// so an admin's active session on the same machine survives a student's
// portal login. The api.js interceptor picks the correct token per request
// path (see isPortalRequest there).

const PortalAuthContext = createContext(null);

export function PortalAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("portal_token");
    const savedUser = localStorage.getItem("portal_user");
    const savedOrg = localStorage.getItem("portal_organization");

    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      if (savedOrg) setOrganization(JSON.parse(savedOrg));

      // Refresh from /student/me — cheaper than /auth/me and doesn't hit
      // admin permission machinery. If it fails we drop the local session.
      API.get("/student/me")
        .then((res) => {
          const nextUser = { ...JSON.parse(savedUser), ...res.data };
          setUser(nextUser);
          localStorage.setItem("portal_user", JSON.stringify(nextUser));
          if (res.data.organization) {
            setOrganization(res.data.organization);
            localStorage.setItem(
              "portal_organization",
              JSON.stringify(res.data.organization)
            );
          }
        })
        .catch((err) => {
          // Backend unreachable — keep the session; see AuthContext.
          if (!err.response) return;
          localStorage.removeItem("portal_token");
          localStorage.removeItem("portal_user");
          localStorage.removeItem("portal_organization");
          localStorage.removeItem("portal_session_id");
          setUser(null);
          setOrganization(null);
        });
    }
    setLoading(false);
  }, []);

  const sendOtp = async (email, name) => {
    const res = await API.post("/auth/student/send-otp", { email, name });
    return res.data;
  };

  const verifyOtp = async (email, otp) => {
    const res = await API.post("/auth/student/verify-otp", { email, otp });

    if (!["participant", "student"].includes(res.data.user.role)) {
      throw new Error("This account is not enabled for the assessment portal.");
    }

    localStorage.setItem("portal_token", res.data.token);
    localStorage.setItem("portal_user", JSON.stringify(res.data.user));
    if (res.data.session_id) {
      localStorage.setItem("portal_session_id", res.data.session_id);
    }

    // Backend returns organization as flat fields on user (mirroring admin
    // verify-otp). Normalize into a nested object for downstream use.
    const orgId = res.data.user.organization_id;
    if (orgId) {
      const org = {
        id: orgId,
        name: res.data.user.organization_name,
        slug: res.data.user.organization_slug,
        logo_url: res.data.user.organization_logo_url || null,
        portal_theme: res.data.user.organization_portal_theme || null,
        portal_theme_colors: res.data.user.organization_portal_theme_colors || null,
      };
      setOrganization(org);
      localStorage.setItem("portal_organization", JSON.stringify(org));
    }

    setUser(res.data.user);
    return res.data;
  };

  const updateProfile = async ({ name }) => {
    const res = await API.put("/student/me", { name });
    // Server returns { id, email, name } — merge into current user so
    // organization/campus fields we already have stay in place.
    setUser((prev) => {
      const next = { ...(prev || {}), ...res.data };
      localStorage.setItem("portal_user", JSON.stringify(next));
      return next;
    });
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("portal_token");
    localStorage.removeItem("portal_user");
    localStorage.removeItem("portal_organization");
    localStorage.removeItem("portal_session_id");
    setUser(null);
    setOrganization(null);
    window.location.href = "/portal/login";
  };

  return (
    <PortalAuthContext.Provider
      value={{
        user,
        organization,
        loading,
        sendOtp,
        verifyOtp,
        updateProfile,
        logout,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </PortalAuthContext.Provider>
  );
}

export function usePortalAuth() {
  const ctx = useContext(PortalAuthContext);
  if (!ctx)
    throw new Error("usePortalAuth must be used within PortalAuthProvider");
  return ctx;
}
